//#region src/claim-check.ts
/**
* Claim-check large-payload marker — cross-SDK payload contract.
*
* The DP event bus is stock NATS with a hard 1 MiB per-message ceiling. When an
* event payload exceeds the offload threshold, the publisher writes the payload
* bytes to blob storage and puts a small REFERENCE on NATS as the event payload;
* the consumer rehydrates the bytes before dispatching to the handler. The
* reference is this marker, and it IS the signed payload (claim-check plan C1):
*
*   payload = { "__claimCheck": { blobKey, contentHash, size, contentType } }
*
* Because the marker rides INSIDE the signed payload, `contentHash` (sha256 of
* the offloaded bytes) is covered by the existing envelope signature — a
* swapped/tampered blob is detected at rehydration by re-hashing. There is NO
* envelope / canonical-signing change (see `signed-envelope.ts`, untouched): the
* publisher signs `JSON.stringify(payload)` exactly as today.
*
* **Cross-SDK shape contract (plan C2/C3/DEC-4/DEC-10).** What is locked across
* Node/Python/Rust is the marker's SHAPE — the reserved key, the four fields,
* their types, their nesting — NOT a byte-canonical JSON form. Field values are
* constrained to **strings + integers only** (no floats, no booleans, no
* non-ASCII), so every SDK's serializer emits byte-stable output and the marker
* inherits the already-proven order-independent signed-envelope verification.
* Never assert `serialize(marker) == a foreign-authored golden byte string`
* (Rust's sorted-key serde would false-fail it) — the signature over the payload
* is the authoritative cross-language control.
*
* **Detection is post-verify only (plan C9).** `isClaimCheckMarker` is a pure,
* org-agnostic shape check. It does NOT recompute the hash, confine `blobKey` to
* an org, validate the `claimcheck/<org>/` prefix, or enforce the max-offload cap
* (DEC-14) — those are the consumer's responsibilities (they need the VERIFIED
* orgId / the resolved cap; plan C5/AC3). Callers MUST run this guard only on a
* payload whose envelope signature already verified.
*
* The contracts package is type-only — keep this file zero-dep.
*/
/** Reserved payload key under which the claim-check marker lives (DEC-21). */
const CLAIM_CHECK_KEY = "__claimCheck";
/** Default `contentType` for offloaded event payloads (plan DEC-4). */
const CLAIM_CHECK_DEFAULT_CONTENT_TYPE = "application/json";
/** Matches a sha256 digest in lowercase hex (64 chars). */
const SHA256_HEX = /^[0-9a-f]{64}$/;
/**
* Type guard: does the (already-verified) payload carry a well-formed
* claim-check marker under the reserved `__claimCheck` key?
*
* Fail-closed: any missing/mistyped field ⇒ `false`. Enforces the plan C2
* value-type constraint (strings + non-negative integer) and the sha256
* lowercase-hex shape of `contentHash`. Org-confinement, hash-recompute, and
* the max-offload cap are the consumer's job (they need the verified orgId / the
* resolved cap) and are intentionally NOT checked here. Extra keys inside the
* marker are not rejected (forward-compat).
*
* Exported for direct unit testing of every branch.
*/
function isClaimCheckMarker(payload) {
	if (typeof payload !== "object" || payload === null) return false;
	if (!Object.prototype.hasOwnProperty.call(payload, "__claimCheck")) return false;
	const marker = payload[CLAIM_CHECK_KEY];
	if (typeof marker !== "object" || marker === null) return false;
	const m = marker;
	if (typeof m.blobKey !== "string" || m.blobKey.length === 0) return false;
	if (typeof m.contentHash !== "string" || !SHA256_HEX.test(m.contentHash)) return false;
	if (typeof m.size !== "number" || !Number.isInteger(m.size) || m.size < 0) return false;
	if (typeof m.contentType !== "string" || m.contentType.length === 0) return false;
	return true;
}
/**
* Shared claim-check DLQ / observability reason strings (plan DEC-20). All three
* SDKs emit these EXACT string values so cross-language operators grep one
* vocabulary. The string values are the cross-SDK contract; Python/Rust mirror
* these identical literals. The retry/DLQ mechanism (bounded-NAK, max_deliver)
* is owned by WS-cc-{node,python,rust}; this only fixes the reason vocabulary.
*/
const CLAIM_CHECK_REASON = {
	/** Rehydrate BlobNotFound after the bounded retry budget is exhausted (C6/AC4) → DLQ. */
	blobMissing: "claim-check-blob-missing",
	/** Fetched bytes' sha256 != marker.contentHash (T1/AC2) → permanent reject. */
	integrity: "claim-check-integrity",
	/** marker.blobKey escapes the verified `claimcheck/<orgId>/` prefix (C5/T2/AC3) → permanent reject. */
	orgViolation: "claim-check-org-violation",
	/** marker.size (or fetched length) > max-offload cap (DEC-14/T6) → permanent reject. */
	oversize: "claim-check-oversize",
	/** No blob store wired for a claim-check payload (C8/DEC-12/DEC-17) → typed error / permanent reject. */
	noStore: "claim-check-no-store"
};
/** All claim-check reason string values, frozen (plan DEC-20). */
const CLAIM_CHECK_REASONS = Object.freeze([
	CLAIM_CHECK_REASON.blobMissing,
	CLAIM_CHECK_REASON.integrity,
	CLAIM_CHECK_REASON.orgViolation,
	CLAIM_CHECK_REASON.oversize,
	CLAIM_CHECK_REASON.noStore
]);
/** Type guard for a claim-check reason string (exported for branch testing). */
function isClaimCheckReason(value) {
	return typeof value === "string" && CLAIM_CHECK_REASONS.includes(value);
}
//#endregion
export { CLAIM_CHECK_DEFAULT_CONTENT_TYPE, CLAIM_CHECK_KEY, CLAIM_CHECK_REASON, CLAIM_CHECK_REASONS, isClaimCheckMarker, isClaimCheckReason };

//# sourceMappingURL=claim-check.js.map